import { format } from 'date-fns';
import { useEffect, useState } from 'react';
import { Input, Select, Button } from '../components/input';
import { Loading } from '../components/loading';
import { LeftNav } from '../components/nav';
import { Error } from '../components/error';
import { useProxyTable } from '../state';
import { proxies } from '../constants';

const padding = '2px 8px';

const numStyle = {
  width: 100,
  fontSize: 12,
  fontFamily: 'monospace',
  textAlign: 'right',
  padding,
};

const fmt = (x) => (100 * (x || 0)).toFixed(1) + '%';

// https://chatgpt.com/share/692a250b-8544-800e-a9be-92ada1f7e441
const color = (p, n) => {
  const clamp = (v, min, max) => (v < min ? min : v > max ? max : v);

  const lerp = (a, b, t) => Math.round(a + (b - a) * t);

  // Normalize p: 0.8 = ok, 0.95 = good
  const pScore = clamp((p - 0.8) / (0.95 - 0.8), 0, 1);

  // Normalize n: 10 = ok, 25 = good
  const nScore = clamp((n - 10) / (25 - 10), 0, 1);

  // Combined confidence
  const score = (pScore + nScore) / 2;

  // Color endpoints
  const start = { r: 255, g: 255, b: 255 };
  const end = { r: 160, g: 235, b: 170 };

  const r = lerp(start.r, end.r, score);
  const g = lerp(start.g, end.g, score);
  const b = lerp(start.b, end.b, score);

  return `rgb(${r}, ${g}, ${b})`;
};

const Choices = ({ choice }) => {
  if (!choice) {
    return null;
  }

  const rows = (choice?.combos || []).map((it, idx) => {
    const backgroundColor = color(it.p || 0, it.n || 0);
    return (
      <tr>
        <td style={{ padding }}>{it.proxy?.tier}</td>
        <td style={{ ...numStyle, backgroundColor }}>{fmt(it.p)}</td>
        <td style={{ ...numStyle, backgroundColor }}>{it.n}</td>
        <td style={numStyle}>{idx < choice.concurrency ? 'yes' : 'no'}</td>
      </tr>
    );
  });

  return (
    <div>
      <h2>Auto proxy</h2>
      <table style={{ width: 300 }}>
        <tbody>
          <tr>
            <td style={{ padding }}>
              <b>Overall p:</b>
            </td>
            <td style={{ textAlign: 'right', padding }}>{fmt(choice?.p)}</td>
          </tr>
          <tr>
            <td style={{ padding }}>
              <b>Overall n:</b>
            </td>
            <td style={{ textAlign: 'right', padding }}>{choice?.n}</td>
          </tr>
          <tr>
            <td style={{ padding }}>
              <b>Concurrency:</b>
            </td>
            <td style={{ textAlign: 'right', padding }}>{choice?.concurrency}</td>
          </tr>
          <tr>
            <td style={{ padding }}>
              <b>Estimated cost:</b>
            </td>
            <td style={{ textAlign: 'right', padding }}>${(choice.cost || 0).toFixed(2)}/gb</td>
          </tr>
        </tbody>
      </table>
      <p>Selected proxies in order:</p>
      <table style={{ width: 'auto' }}>
        <thead>
          <tr>
            <th style={{ widt: 200, padding }}>tier</th>
            <th style={{ widt: 120, padding }}>p</th>
            <th style={{ widt: 120, padding }}>n</th>
            <th style={{ widt: 120, padding }}>first pass?</th>
          </tr>
        </thead>
        <tbody>{rows}</tbody>
      </table>
    </div>
  );
};

export default function ProxyTablePage() {
  const [url, setUrl] = useState('https://pokemondb.net/pokedex/national');
  const [error, setError] = useState();
  const [domain, setDomain] = useState();
  const [days, setDays] = useState(10);
  const { loading, table, choice, update } = useProxyTable();

  const choices = [
    [1, 'Last 1 day'],
    [2, 'Last 2 days'],
    [5, 'Last 5 days'],
    [10, 'Last 10 days'],
    [20, 'Last 20 days'],
    [30, 'Last 30 days'],
  ];

  const handleSubmit = (e) => {
    e.preventDefault();
    setError(null);
    try {
      setDomain(new URL(url).origin);
    } catch {
      setError('Invalid URL');
      return;
    }
    update(url, days);
  };

  useEffect(() => {
    update(url, days);
  }, [days]);

  const sf = new Set();
  table.forEach((it) => Object.keys(it.features || {}).forEach((f) => sf.add(f)));
  const features = [...sf];

  const ss = new Set(['ok']);
  table.forEach((it) => Object.keys(it.counts?.byStatus || {}).forEach((f) => ss.add(f)));
  const statuses = [...ss];
  const headerStyle = {
    padding,
  };

  const headers = [];
  for (const f of [...features]) {
    headers.push(
      <th style={headerStyle} key={f}>
        {f}
      </th>,
    );
  }
  headers.push(
    ...[
      <th style={headerStyle} key="p">
        p
      </th>,
      <th style={headerStyle} key="n">
        n
      </th>,
      <th style={headerStyle} key="ci">
        ci
      </th>,
    ],
  );
  for (const s of [...statuses]) {
    headers.push(
      <th style={headerStyle} key={s}>
        {s}
      </th>,
    );
  }

  const rows = table.map((it) => {
    const backgroundColor = color(it.p || 0, it.n || 0);
    return (
      <tr>
        {features.map((f) => (
          <td style={{ width: 120, padding }}>{it.features[f]}</td>
        ))}
        <td style={{ ...numStyle, backgroundColor }}>{fmt(it.p)}</td>
        <td style={{ ...numStyle, backgroundColor }}>{it.n}</td>
        <td style={{ ...numStyle, width: 160 }}>
          {fmt(it.ci.lower)} - {fmt(it.ci.upper)}
        </td>
        {statuses.map((s) => (
          <td style={numStyle}>{(it.counts?.byStatus || {})[s]}</td>
        ))}
      </tr>
    );
  });

  return (
    <div>
      <form onSubmit={handleSubmit}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: 5 }}>
          <div>
            <Input
              placeholder="https://example.com"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              style={{ width: 600 }}
            />
          </div>
          <div>
            <Select noEmpty value={days} onChange={setDays} choices={choices} />
          </div>
          <Button>Submit</Button>
          <div>{loading && <Loading />}</div>
        </div>
      </form>
      <Error message={error} />
      <p>{domain}</p>

      <table style={{ width: 'auto' }}>
        <thead>
          <tr>{headers}</tr>
        </thead>
        <tbody>{rows}</tbody>
      </table>

      <br />

      <Choices choice={choice} />

      <br />
      <br />

      {/*
      <pre>{JSON.stringify(choice, null, 2)}</pre>
      <pre>{JSON.stringify(table, null, 2)}</pre>
       */}
    </div>
  );
}
